import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { Box, LinearProgress, Snackbar, Alert, Grid, Typography } from '@mui/material';
import { detectUserLocation } from '../../store/actions/uiActions';
import { fetchEventsByTimeFilter, setTimeFilter } from '../../store/actions/eventsActions';
import MapComponent from '../map/MapComponent';
import FilterPanel from '../filters/FilterPanel';
import InfoCard from '../map/InfoCard';
import MapControls from '../map/MapControls';
import { getTranslation } from '../../i18n';

const MapPage = () => {
  const dispatch = useDispatch();
  const { eventId } = useParams();
  const { location, loading: uiLoading, language } = useSelector((state) => state.ui);
  const { events, loading: eventsLoading, error, timeFilter } = useSelector((state) => state.events);
  const [mapBounds, setMapBounds] = useState(null);
  const [errorOpen, setErrorOpen] = useState(false);
  const [mapMethods, setMapMethods] = useState(null);
  const [infoCardOpen, setInfoCardOpen] = useState(false);
  const translations = getTranslation(language);
  const t = translations?.footer || {};

  // Initialize user location
  useEffect(() => {
    if (!location) {
      dispatch(detectUserLocation());
    }
  }, [dispatch, location]);

  // Fetch events when location or time filter changes
  useEffect(() => {
    if (location && mapBounds) {
      dispatch(fetchEventsByTimeFilter(mapBounds));
    }
  }, [dispatch, location, timeFilter, mapBounds]);

  // Show error snackbar when there's an error
  useEffect(() => {
    if (error) {
      setErrorOpen(true);
    }
  }, [error]);

  // Handle map bounds change
  const handleBoundsChange = (bounds) => {
    // Convert bounds to bbox format [west, south, east, north]
    const bbox = [
      bounds.west,
      bounds.south,
      bounds.east,
      bounds.north,
    ];
    setMapBounds(bbox);
  };

  // Handle time filter change
  const handleTimeFilterChange = (newTimeFilter) => {
    dispatch(setTimeFilter(newTimeFilter));
  };

  // Handle error snackbar close
  const handleErrorClose = () => {
    setErrorOpen(false);
  };

  return (
    <Box className="map-page" sx={{ height: '100%', position: 'relative' }}>
      {/* Loading indicator */}
      {(uiLoading || eventsLoading) && (
        <LinearProgress className="loading-indicator" />
      )}

      {/* Map component */}
      {location && (
        <MapComponent
          center={location}
          events={events}
          onBoundsChange={handleBoundsChange}
          focusedEventId={eventId}
          onMapReady={setMapMethods}
        />
      )}

      {/* Filter panel */}
      <FilterPanel
        timeFilter={timeFilter}
        onTimeFilterChange={handleTimeFilterChange}
      />

      {/* Info card with map legend */}
      <InfoCard 
        isOpen={infoCardOpen} 
        onClose={() => setInfoCardOpen(false)} 
      />

      {/* Map controls */}
      <MapControls
        onLocateMe={() => dispatch(detectUserLocation())}
        onZoomIn={() => mapMethods && mapMethods.zoomIn()}
        onZoomOut={() => mapMethods && mapMethods.zoomOut()}
        onInfoToggle={() => setInfoCardOpen(!infoCardOpen)}
      />

      {/* Error snackbar */}
      <Snackbar
        open={errorOpen}
        autoHideDuration={6000}
        onClose={handleErrorClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={handleErrorClose} severity="error">
          {error}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default MapPage;
